import { useEffect, useRef, useState } from "react";
import { Body } from "@songtradr-one/components-frontend";
import { endpoints } from "@songtradr-one/shared-common";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import "@uppy/core/css/style.min.css";
import "@uppy/dashboard/css/style.min.css";
import Dropbox from "@uppy/dropbox";
import GoogleDrive from "@uppy/google-drive";
import { Dashboard as DashboardReact } from "@uppy/react";
import { Api } from "../../api/api";
import { useAppContext } from "../../context/app-context/AppContext";
import { Flex } from "../flex/Flex.Parts";
import {
  DEFAULT_MAX_FILE_SIZE,
  FileUploaderProps,
  MULTIPART_THRESHOLD,
  S3_PLUGIN_ID,
  UppyWrapper,
  getAllowedMetaFields,
} from "./FileUploader.Parts";

export const FileUploader = ({
  id,
  bucketKey,
  metadata,
  onUploadSuccess,
  onUploadError,
  onAllUploadsComplete,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxNumberOfFiles,
  allowedFileTypes,
  description,
  cloudProviders = true,
}: FileUploaderProps) => {
  const { appConfig } = useAppContext();

  // Refs so the Uppy callbacks, created once below, see the latest props
  const onUploadSuccessRef = useRef(onUploadSuccess);
  onUploadSuccessRef.current = onUploadSuccess;
  const onUploadErrorRef = useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;
  const onAllUploadsCompleteRef = useRef(onAllUploadsComplete);
  onAllUploadsCompleteRef.current = onAllUploadsComplete;
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;
  const bucketKeyRef = useRef(bucketKey);
  bucketKeyRef.current = bucketKey;
  // S3 key per file from the presigned params, passed on to onUploadSuccess
  const s3KeyByFileIdRef = useRef(new Map<string, string | undefined>());

  const [uppy] = useState(() => {
    const uppyInstance = new Uppy({
      id,
      autoProceed: true,
      restrictions: { maxFileSize, maxNumberOfFiles, allowedFileTypes },
    });

    const coreBackendUrl = appConfig.coreBackendUrl;
    const companionUrl = `${coreBackendUrl}${endpoints.companion.base}`;
    const companionConfig = {
      companionUrl,
      companionCookiesRule: "include" as const,
      companionHeaders: {
        "x-app-id": Api.defaults.headers.common["x-app-id"] as string,
      },
    };

    if (cloudProviders) {
      uppyInstance.use(GoogleDrive, companionConfig);
      uppyInstance.use(Dropbox, companionConfig);
    }

    uppyInstance.on("file-added", (file) => {
      uppyInstance.setFileMeta(file.id, {
        bucketKey: bucketKeyRef.current,
        ...metadataRef.current,
      });
    });

    uppyInstance.use(AwsS3, {
      id: S3_PLUGIN_ID,
      endpoint: companionUrl,
      headers: companionConfig.companionHeaders,
      cookiesRule: "include",
      allowedMetaFields: getAllowedMetaFields(metadataRef.current),
      shouldUseMultipart: (file) => (file.size ?? 0) > MULTIPART_THRESHOLD,
      async getUploadParameters(file) {
        const params = new URLSearchParams({
          filename: file.name || "unnamed-file",
          type: file.type || "application/octet-stream",
        });
        params.set("metadata[bucketKey]", bucketKeyRef.current);
        for (const [key, value] of Object.entries(metadataRef.current ?? {})) {
          params.set(`metadata[${key}]`, value);
        }

        const { data } = await Api.get(
          `${coreBackendUrl}${endpoints.companion.s3Params}`,
          { params },
        );

        s3KeyByFileIdRef.current.set(file.id, data.fields?.key);

        return {
          method: data.method,
          url: data.url,
          fields: data.fields,
          headers: data.headers || {},
        };
      },
    });

    // Multipart uploads skip getUploadParameters; their key is in the response body
    uppyInstance.on("upload-success", (file, response) => {
      const responseKey = response?.body?.["key"];
      onUploadSuccessRef.current?.(
        file?.name || "unknown-file",
        file?.type ?? undefined,
        file?.size ?? undefined,
        file
          ? typeof responseKey === "string"
            ? responseKey
            : s3KeyByFileIdRef.current.get(file.id)
          : undefined,
      );
    });

    uppyInstance.on("upload-error", (file, error) => {
      onUploadErrorRef.current?.(
        new Error(
          `Upload failed for ${file?.name || "unknown-file"}: ${error.message}`,
        ),
      );
    });

    uppyInstance.on("complete", () => {
      onAllUploadsCompleteRef.current?.();
      uppyInstance.cancelAll();
    });

    uppyInstance.on("restriction-failed", (file, error) => {
      onUploadErrorRef.current?.(
        new Error(
          `File restriction for ${file?.name || "unknown-file"}: ${error.message}`,
        ),
      );
    });

    return uppyInstance;
  });

  // Keep the S3 plugin's allowed meta fields in line with later metadata keys
  const metadataKeys = Object.keys(metadata ?? {})
    .sort()
    .join(",");
  useEffect(() => {
    uppy.getPlugin(S3_PLUGIN_ID)?.setOptions({
      allowedMetaFields: getAllowedMetaFields(metadataRef.current),
    });
  }, [uppy, metadataKeys]);

  return (
    <Flex
      $direction="column"
      $gap="1rem"
      $flex={1}
      style={{ minHeight: 0, overflow: "hidden" }}
    >
      {description && <Body>{description}</Body>}
      <UppyWrapper>
        <DashboardReact
          uppy={uppy}
          width={"100%"}
          height={"100%"}
          proudlyDisplayPoweredByUppy={false}
          theme="dark"
        />
      </UppyWrapper>
    </Flex>
  );
};
